import { useState } from "react";
import { supabase } from "../supabaseClient";
import { usePlan, PLAN_LABELS } from "../hooks/usePlan";

const PRICE_IDS = {
  solo: "price_1TDVyIB6VkuKi8rnY8AEBPQs",
  studio: "price_1TDW2XB6VkuKi8rnzYp91oEo",
  premium: "price_1TDW4GB6VkuKi8rnIO0FDytG",
  lifetime: "price_1TDW5kB6VkuKi8rn5zbUXJm4",
};

async function handleCheckout(priceId, isLifetime = false) {
  try {
    // Vérifie que la session est active avant d'invoquer la Edge Function.
    // Un token absent ou expiré ferait rejeter la requête par le relay
    // Supabase avant même que la fonction ne tourne (→ EarlyDrop).
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      alert("Session expirée. Veuillez vous reconnecter.");
      return;
    }

    // On passe userId + email explicitement dans le body pour éviter
    // que la Edge Function ait besoin d'imports Supabase (→ EarlyDrop)
    const { data, error } = await supabase.functions.invoke("stripe-checkout", {
      body: {
        priceId,
        mode: isLifetime ? "payment" : "subscription",
        userId: session.user.id,
        email: session.user.email,
      },
    });

    if (error) {
      console.error("stripe-checkout error:", error);
      alert("Erreur lors de la redirection vers le paiement : " + (error.message ?? String(error)));
      return;
    }
    if (!data?.url) {
      console.error("stripe-checkout: url manquante", data);
      alert("Erreur : URL de paiement manquante. Vérifiez la clé Stripe dans les secrets Supabase.");
      return;
    }

    window.location.href = data.url;
  } catch (err) {
    console.error("stripe-checkout exception:", err);
    alert("Erreur : " + err.message);
  }
}

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#D8C9B8";
const VERT = "#5C8A5C";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

const mailtoBase = (subject, body) =>
  `mailto:contact@yoma.design?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const FAQ_ITEMS = [
  {
    q: "Puis-je changer de forfait ?",
    a: "Oui, à tout moment sans frais.",
  },
  {
    q: "Que se passe-t-il après l'essai ?",
    a: "Vos données sont conservées. Choisissez un forfait pour continuer.",
  },
  {
    q: "Le Lifetime inclut-il les futures fonctionnalités ?",
    a: "Oui, toutes les mises à jour pendant 1 an incluses.",
  },
];

const COMPARISON_ROWS = [
  { label: "Projets actifs", solo: "3", studio: "10", premium: "Illimité", lifetime: "Illimité" },
  { label: "Utilisateurs", solo: "1", studio: "3", premium: "Illimité", lifetime: "Illimité" },
  { label: "Module Conception", solo: true, studio: true, premium: true, lifetime: true },
  { label: "Module OPC", solo: false, studio: true, premium: true, lifetime: true },
  { label: "Module Mandataire", solo: false, studio: true, premium: true, lifetime: true },
  { label: "Contrats intelligents", solo: true, studio: true, premium: true, lifetime: true },
  { label: "IA intégrée", solo: true, studio: true, premium: true, lifetime: true },
  { label: "Espace entreprises", solo: false, studio: true, premium: true, lifetime: true },
  { label: "Branding personnalisé", solo: false, studio: false, premium: true, lifetime: true },
  { label: "Support", solo: "Email", studio: "Prioritaire", premium: "Dédié", lifetime: "Prioritaire à vie" },
];

function Cell({ v }) {
  if (typeof v === "boolean") {
    return (
      <span style={{ fontSize: 16, fontWeight: 700, color: v ? VERT : MUTED }}>{v ? "✓" : "—"}</span>
    );
  }
  return <span style={{ fontSize: 13, fontWeight: 600, color: TAUPE, fontFamily: FF_DM }}>{v}</span>;
}

export default function UpgradePage() {
  const { plan: planActuel } = usePlan();
  const [faqOpen, setFaqOpen] = useState(null);

  const trialHref = mailtoBase(
    "Essai gratuit 10 jours — YOMA",
    "Bonjour,\n\nJe souhaite démarrer mon essai gratuit de 10 jours sur YOMA.\n\nCordialement,"
  );

  return (
    <><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        `}
      </style><div
        style={{
          fontFamily: FF_DM,
          color: TAUPE,
          background: BG,
          minHeight: "100%",
          padding: "32px 20px 48px",
          boxSizing: "border-box",
        }}
      ><div style={{ maxWidth: 1180, margin: "0 auto" }}>
          {/* HEADER */}
          <header style={{ textAlign: "center", marginBottom: 36 }}><div
              style={{
                fontFamily: FF_PLAYFAIR,
                fontSize: 40,
                fontWeight: 700,
                color: GOLD,
                letterSpacing: "0.04em",
                marginBottom: 10,
              }}
            >
              YOMA
            </div><p style={{ margin: "0 0 16px", fontSize: 17, color: TAUPE, fontWeight: 500, maxWidth: 520, marginLeft: "auto", marginRight: "auto" }}>
              La plateforme des professionnels de l&apos;espace
            </p><a
              href="https://yoma.design"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                marginBottom: 14,
                color: GOLD,
                textDecoration: "underline",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Découvrir toutes les fonctionnalités → yoma.design
            </a><div
              style={{
                display: "inline-block",
                padding: "8px 18px",
                borderRadius: 999,
                background: "rgba(192,154,91,0.15)",
                border: `1px solid rgba(192,154,91,0.45)`,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: "#7A5F2A",
              }}
            >
              Essai gratuit 10 jours — sans engagement
            </div>
            {planActuel && (
              <p style={{ margin: "14px 0 0", fontSize: 12, color: MUTED }}>
                Forfait actuel : <strong style={{ color: TAUPE }}>{PLAN_LABELS[planActuel] || planActuel}</strong></p>
            )}
          </header>

          {/* BANNIÈRE ESSAI */}
          <section
            style={{
              background: BG_CARD,
              borderRadius: 18,
              padding: "28px 32px 30px",
              border: `2px solid ${GOLD}`,
              boxShadow: "0 8px 32px rgba(192,154,91,0.15)",
              marginBottom: 40,
            }}
          ><h2
              style={{
                margin: "0 0 18px",
                fontFamily: FF_PLAYFAIR,
                fontSize: 24,
                fontWeight: 700,
                color: TAUPE,
                lineHeight: 1.35,
                maxWidth: 640,
              }}
            >
              10 jours pour découvrir toute la puissance de YOMA
            </h2><div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 12,
                marginBottom: 16,
              }}
            >
              {["Accès complet", "IA intégrée", "Contrats intelligents", "Questionnaire client"].map((t) => (
                <div
                  key={t}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: TAUPE,
                  }}
                ><span style={{ color: GOLD, fontWeight: 700 }}>✓</span> {t}
                </div>
              ))}
            </div><p style={{ margin: "0 0 22px", fontSize: 12, color: MUTED, fontStyle: "italic", lineHeight: 1.5 }}>
              Documents en mode DÉMO — non utilisables professionnellement
            </p><a
              href={trialHref}
              style={{
                display: "block",
                width: "100%",
                maxWidth: 420,
                textAlign: "center",
                padding: "14px 24px",
                borderRadius: 12,
                background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                color: "#FFF",
                fontSize: 15,
                fontWeight: 700,
                textDecoration: "none",
                boxShadow: "0 4px 20px rgba(192,154,91,0.45)",
                fontFamily: FF_DM,
              }}
            >
              Démarrer mon essai gratuit
            </a></section>

          {/* GRILLE 4 FORFAITS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              marginBottom: 48,
              alignItems: "stretch",
            }}
          >
            {/* SOLO */}
            <article
              style={{
                background: BG_CARD,
                borderRadius: 16,
                padding: "24px 22px",
                border: `1px solid ${BORDER}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            ><div style={{ marginBottom: 12 }}><span
                  style={{
                    display: "inline-block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "rgba(61,46,32,0.08)",
                    color: TAUPE,
                  }}
                >
                  Indépendants
                </span></div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: TAUPE, marginBottom: 4 }}>SOLO</div><div style={{ fontSize: 22, fontWeight: 700, color: GOLD, marginBottom: 8 }}>99€/mois</div><p style={{ margin: "0 0 16px", fontSize: 13, color: MUTED, lineHeight: 1.45 }}>Pour : Architecte intérieur, Décorateur</p><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: TAUPE, lineHeight: 1.65, flex: 1 }}><li>3 projets actifs</li><li>1 utilisateur</li><li>Modules Conception + Déco + Contrats</li><li>Questionnaire client</li><li>Support email</li></ul>
              <button
                type="button"
                onClick={() => handleCheckout(PRICE_IDS.solo)}
                style={{
                  marginTop: 20,
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: `2px solid ${GOLD}`,
                  color: TAUPE,
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: FF_DM,
                }}
              >
                Choisir Solo
              </button>
            </article>

            {/* STUDIO — mis en avant */}
            <article
              style={{
                background: BG_CARD,
                borderRadius: 16,
                padding: "24px 22px",
                border: `3px solid ${GOLD}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 16px 48px rgba(192,154,91,0.25)",
                position: "relative",
                transform: "scale(1.02)",
                zIndex: 2,
              }}
            ><div style={{ marginBottom: 12 }}><span
                  style={{
                    display: "inline-block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    padding: "5px 12px",
                    borderRadius: 999,
                    background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                    color: "#FFF",
                  }}
                >
                   Le plus populaire
                </span></div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: TAUPE, marginBottom: 4 }}>STUDIO</div><div style={{ fontSize: 22, fontWeight: 700, color: GOLD, marginBottom: 8 }}>199€/mois</div><p style={{ margin: "0 0 16px", fontSize: 13, color: MUTED, lineHeight: 1.45 }}>Pour : Cabinet OPC, Conducteur de travaux</p><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: TAUPE, lineHeight: 1.65, flex: 1 }}><li>10 projets actifs</li><li>3 utilisateurs</li><li>Tous les modules</li><li>Espace entreprises</li><li>Messagerie interne</li><li>Support prioritaire</li></ul>
              <button
                type="button"
                onClick={() => handleCheckout(PRICE_IDS.studio)}
                style={{
                  marginTop: 20,
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                  boxShadow: "0 4px 16px rgba(192,154,91,0.45)",
                  cursor: "pointer",
                  fontFamily: FF_DM,
                  border: "none",
                }}
              >
                Choisir Studio
              </button>
            </article>

            {/* PREMIUM */}
            <article
              style={{
                background: BG_CARD,
                borderRadius: 16,
                padding: "24px 22px",
                border: `1px solid ${BORDER}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            ><div style={{ marginBottom: 12 }}><span
                  style={{
                    display: "inline-block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "rgba(61,46,32,0.08)",
                    color: TAUPE,
                  }}
                >
                  Cabinets &amp; Promoteurs
                </span></div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: TAUPE, marginBottom: 4 }}>PREMIUM</div><div style={{ fontSize: 22, fontWeight: 700, color: GOLD, marginBottom: 8 }}>399€/mois</div><p style={{ margin: "0 0 16px", fontSize: 13, color: MUTED, lineHeight: 1.45 }}>Pour : Promoteur, Cabinet multi-missions</p><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: TAUPE, lineHeight: 1.65, flex: 1 }}><li>Projets illimités</li><li>Utilisateurs illimités</li><li>Tout débloqué</li><li>Branding personnalisé</li><li>Support dédié</li></ul>
              <button
                type="button"
                onClick={() => handleCheckout(PRICE_IDS.premium)}
                style={{
                  marginTop: 20,
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: `2px solid ${GOLD}`,
                  color: TAUPE,
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: FF_DM,
                }}
              >
                Choisir Premium
              </button>
            </article>

            {/* LIFETIME */}
            <article
              style={{
                background: TAUPE,
                borderRadius: 16,
                padding: "24px 22px",
                border: `1px solid ${TAUPE}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 12px 40px rgba(61,46,32,0.35)",
              }}
            ><div style={{ marginBottom: 12 }}><span
                  style={{
                    display: "inline-block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    padding: "5px 12px",
                    borderRadius: 999,
                    background: "rgba(192,154,91,0.25)",
                    color: "#F5E6CC",
                    border: "1px solid rgba(192,154,91,0.5)",
                  }}
                >
                  Meilleure valeur
                </span></div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: "#F8F4EE", marginBottom: 4 }}>LIFETIME</div><div style={{ fontSize: 22, fontWeight: 700, color: GOLD, marginBottom: 8 }}>2 900€ paiement unique</div><p style={{ margin: "0 0 16px", fontSize: 13, color: "rgba(248,244,238,0.75)", lineHeight: 1.45 }}>Pour : Professionnels établis</p><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#F0E8DC", lineHeight: 1.65, flex: 1 }}><li>Accès illimité à vie</li><li>Tout débloqué pour toujours</li><li>Mises à jour incluses 1 an</li><li>Support prioritaire à vie</li><li>Équivalent 7 mois de Premium</li></ul>
              <button
                type="button"
                onClick={() => handleCheckout(PRICE_IDS.lifetime, true)}
                style={{
                  marginTop: 20,
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: "none",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                  cursor: "pointer",
                  fontFamily: FF_DM,
                  border: "none",
                }}
              >
                Acheter Lifetime
              </button>
            </article>
          </div>

          {/* TABLEAU COMPARATIF */}
          <section style={{ marginBottom: 48 }}><h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: TAUPE, marginBottom: 20, textAlign: "center" }}>
              Comparer les fonctionnalités
            </h2><div style={{ overflowX: "auto", borderRadius: 14, border: `1px solid ${BORDER}`, background: BG_CARD }}><table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontFamily: FF_DM }}><thead><tr style={{ background: "rgba(192,154,91,0.1)" }}><th
                      style={{
                        textAlign: "left",
                        padding: "14px 16px",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: MUTED,
                        borderBottom: `1px solid ${BORDER}`,
                      }}
                    >
                      Fonctionnalité
                    </th>
                    {["Solo", "Studio", "Premium", "Lifetime"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: "center",
                          padding: "14px 12px",
                          fontSize: 12,
                          fontWeight: 700,
                          color: h === "Studio" ? GOLD : TAUPE,
                          borderBottom: `1px solid ${BORDER}`,
                          fontFamily: FF_PLAYFAIR,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr></thead><tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={row.label} style={{ background: i % 2 === 0 ? BG_CARD : "rgba(248,244,238,0.6)" }}><td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: TAUPE, borderBottom: `1px solid ${BORDER}` }}>{row.label}</td><td style={{ textAlign: "center", borderBottom: `1px solid ${BORDER}` }}><Cell v={row.solo} /></td><td style={{ textAlign: "center", borderBottom: `1px solid ${BORDER}`, background: "rgba(192,154,91,0.06)" }}><Cell v={row.studio} /></td><td style={{ textAlign: "center", borderBottom: `1px solid ${BORDER}` }}><Cell v={row.premium} /></td><td style={{ textAlign: "center", borderBottom: `1px solid ${BORDER}`, background: "rgba(61,46,32,0.04)" }}><Cell v={row.lifetime} /></td></tr>
                  ))}
                </tbody></table></div></section>

          {/* POURQUOI YOMA */}
          <section style={{ marginBottom: 48 }}><h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: TAUPE, marginBottom: 20, textAlign: "center" }}>
              Pourquoi YOMA
            </h2><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {[
                {
                  title: "La seule app qui combine 3 métiers",
                  text: "Conception, OPC et mandataire réunis dans un seul flux de travail.",
                },
                {
                  title: "IA à chaque étape de votre mission",
                  text: "Assistants et génération de contenu pour gagner du temps sans sacriger la qualité.",
                },
                {
                  title: "Contrats juridiquement sécurisés",
                  text: "Modèles structurés et clauses adaptées aux missions réelles du bâtiment haut de gamme.",
                },
              ].map((card) => (
                <div
                  key={card.title}
                  style={{
                    background: BG_CARD,
                    borderRadius: 14,
                    padding: "22px 22px",
                    border: `1px solid ${BORDER}`,
                    boxShadow: "0 4px 18px rgba(61,46,32,0.06)",
                  }}
                ><h3 style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, color: TAUPE, margin: "0 0 10px", lineHeight: 1.3 }}>{card.title}</h3><p style={{ margin: 0, fontSize: 14, color: MUTED, lineHeight: 1.55 }}>{card.text}</p></div>
              ))}
            </div></section>

          {/* FAQ */}
          <section style={{ marginBottom: 48, maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}><h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: TAUPE, marginBottom: 20, textAlign: "center" }}>
              Questions fréquentes
            </h2><div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {FAQ_ITEMS.map((item, idx) => {
                const open = faqOpen === idx;
                return (
                  <div
                    key={idx}
                    style={{
                      borderRadius: 12,
                      border: `1px solid ${BORDER}`,
                      background: BG_CARD,
                      overflow: "hidden",
                    }}
                  ><button
                      type="button"
                      onClick={() => setFaqOpen(open ? null : idx)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "16px 18px",
                        border: "none",
                        background: open ? "rgba(192,154,91,0.08)" : BG_CARD,
                        cursor: "pointer",
                        fontFamily: FF_DM,
                        fontSize: 15,
                        fontWeight: 700,
                        color: TAUPE,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      {item.q}
                      <span style={{ color: GOLD, fontSize: 18, flexShrink: 0 }}>{open ? "−" : "+"}</span></button>
                    {open && (
                      <div style={{ padding: "0 18px 16px", fontSize: 14, color: MUTED, lineHeight: 1.6 }}>{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div></section>

          {/* FOOTER */}
          <footer
            style={{
              textAlign: "center",
              paddingTop: 28,
              borderTop: `1px solid ${BORDER}`,
              fontSize: 13,
              color: MUTED,
              lineHeight: 1.8,
            }}
          ><p style={{ margin: "0 0 6px" }}>
              Une question ?{" "}
              <a href="mailto:contact@yoma.design" style={{ color: GOLD, fontWeight: 600, textDecoration: "none" }}>
                contact@yoma.design
              </a></p><p style={{ margin: "0 0 6px", fontSize: 12 }}>Édité par YOMA DESIGN SARL AU</p><p style={{ margin: 0, fontStyle: "italic", fontSize: 13 }}>La plateforme des professionnels de l&apos;espace</p></footer></div></div></>
  );
}
